import type { Queue } from 'utils/queue/config/types';
import { noop } from 'utils/noop';

/**
 * Create a Queue.
 *
 * Warning: this queue will wait for any asynchronous entries to resolve before
 * moving onto the next entry in the queue unless syncFlush is called explicitly.
 *
 * @example
 * ```ts
 * const queue = createQueue();
 *
 * const firstPromise = queue.add(firstAsyncFunction, 1, 2);
 * const secondPromise = queue.add(secondFunction, 3);
 *
 * function firstAsyncFunction(firstNum, secondNum) {
 *   return Promise.resolve([firstNum, secondNum])
 *      .then(res => console.log(res));
 * }
 *
 * function secondFunction(num) {
 *   console.log(num);
 * }
 * ```
 * outputs:
 * ```ts
 *  [1, 2]
 *  3 // execution of second function is queued, even though first is async.
 * ```
 *
 * firstPromise and secondPromise resolve when each of their functions returns/resolves.
 */
export function createQueue(autoFlush: boolean = true): Queue.API {
  let entries: Queue.Entry[] = [];
  let flushing = false;

  return {
    add,
    clear,
    flush,
    syncFlush,
    getEntries,
  };

  function add(fn: Function, ...params: any[]): Promise<any>;
  function add(options: Queue.AddOptions, fn: Function, ...params: any[]): Promise<any>;
  /**
   * Add a function to the queue. A promise is returned from the
   * add function, and resolves (or rejects) when the entry is called.
   */
  function add(
    optionsOrFn: Function | Queue.AddOptions,
    ...paramsOrFnThenParams: any[]
  ): Promise<any> {
    let fn: Function;
    let params: any[];
    let options: Queue.AddOptions = {};

    if (typeof optionsOrFn === 'function') {
      // add(fn, ...params)
      fn = optionsOrFn;
      params = paramsOrFnThenParams;
    } else {
      // add(options, fn, ...params)
      fn = paramsOrFnThenParams[0];
      params = paramsOrFnThenParams.slice(1);
      options = optionsOrFn;
    }

    let rej: Function = noop;
    let res: Function = noop;

    const promise = new Promise((resolve, reject) => {
      res = resolve;
      rej = reject;
    });

    entries.push({
      fn,
      params,
      reject: rej,
      resolve: res,
      preserved: options.preserved ?? false,
    });

    if (autoFlush && flushing === false) {
      flush();
    }

    return promise;
  }

  /**
   * Clear the queue.
   */
  function clear(options: Queue.ClearOptions = {}) {
    flushing = false;

    if (options.persistPreserved) {
      entries = entries.filter((entry) => entry.preserved);
    } else {
      entries = [];
    }
  }

  /**
   * Flush the queue.
   */
  async function flush(): Promise<any> {
    const entry = entries.shift();

    if (!entry) {
      flushing = false;

      return;
    }

    flushing = true;

    try {
      const result = await entry.fn(...entry.params);
      entry.resolve(result);

      return flush();
    } catch (e) {
      entry.reject(e);
    }
  }

  /**
   * Flush the queue synchronously. Does not wait for asynchronous entries to
   * resolve before moving on to the next entry.
   */
  function syncFlush(): void {
    const entry = entries.shift();

    if (!entry) {
      flushing = false;
      return;
    }

    flushing = true;
    try {
      const result = entry.fn(...entry.params);
      if (result instanceof Promise) {
        result.then((value) => entry.resolve(value)).catch((e) => entry.reject(e));
      } else {
        entry.resolve(result);
      }

      syncFlush();
    } catch (e) {
      entry.reject(e);
    }
  }

  /**
   * Get the list of queued entries.
   */
  function getEntries(): Queue.Entry[] {
    return entries;
  }
}
